
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Clock, AlertTriangle } from "lucide-react";
import { format, formatDistance } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { CallAudioSection } from "./CallAudioSection";
import { CallTranscriptSection } from "./CallTranscriptSection";
import { CallIntentSection } from "./CallIntentSection";
import { CallStatusBadge } from "./CallStatusBadge";
import { CallStatusInfoCard } from "./CallStatusInfoCard";
import { NotFoundCard } from "./NotFoundCard";

interface CallResultsDisplayProps {
  callId: string;
}

interface CallDetails {
  id: string;
  lead_id: string;
  status: string;
  transcript: string | null;
  audio_url: string | null;
  intent_summary: string | null;
  intent_score: number | null;
  created_at: string;
  meta?: {
    duration?: number;
    answer_time?: string;
    end_time?: string;
    hangup_cause?: string;
  };
}

const CallResultsDisplay = ({ callId }: CallResultsDisplayProps) => {
  const [callDetails, setCallDetails] = useState<CallDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchCallDetails = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from("calls")
          .select("*")
          .eq("id", callId)
          .single();
        
        if (error) throw error;
        
        setCallDetails(data);
      } catch (err) {
        console.error("Error fetching call details:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch call details");
      } finally {
        setLoading(false);
      }
    };
    
    const setupRealtimeSubscription = () => {
      const channel = supabase
        .channel('call-updates')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'calls',
            filter: `id=eq.${callId}`
          },
          (payload) => {
            console.log("Call updated:", payload);
            setCallDetails(payload.new as CallDetails);
          }
        )
        .subscribe();
      
      return channel;
    };
    
    if (callId) {
      fetchCallDetails();
      const channel = setupRealtimeSubscription();
      
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [callId]);
  
  if (loading) {
    return <LoadingState />;
  }
  
  if (error) {
    return <ErrorState message={error} />;
  }
  
  if (!callDetails) {
    return <NotFoundCard 
      message="Call Not Found" 
      description="No call details found with this ID." 
    />;
  }
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Call Results</CardTitle>
              <CardDescription>
                <span className="flex items-center gap-1 mt-1">
                  <Clock className="h-3.5 w-3.5" />
                  {callDetails.created_at && (
                    <>
                      {format(new Date(callDetails.created_at), "PPP")} • 
                      {formatDistance(new Date(callDetails.created_at), new Date(), { addSuffix: true })}
                    </>
                  )}
                </span>
              </CardDescription>
            </div>
            <CallStatusBadge status={callDetails.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Call Status Information */}
          <CallStatusInfoCard 
            status={callDetails.status} 
            createdAt={callDetails.created_at}
            meta={callDetails.meta}
          />
          
          <Separator />
          
          {/* Audio player */}
          {callDetails.audio_url ? (
            <CallAudioSection audioUrl={callDetails.audio_url} />
          ) : (
            <div className="bg-muted/20 rounded-md p-4 text-sm text-muted-foreground text-center py-8">
              <p>No audio recording available for this call</p>
            </div>
          )}
          
          <Separator />
          
          {/* Transcript */}
          <CallTranscriptSection transcript={callDetails.transcript} />
          
          <Separator />
          
          {/* Intent Analysis */}
          <CallIntentSection 
            intentSummary={callDetails.intent_summary} 
            intentScore={callDetails.intent_score} 
          />
        </CardContent>
      </Card>
    </div>
  );
};

// Extracted component states
const LoadingState = () => (
  <div className="flex justify-center p-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const ErrorState = ({ message }: { message: string }) => (
  <Alert variant="destructive">
    <AlertTriangle className="h-4 w-4" />
    <AlertTitle>Error</AlertTitle>
    <AlertDescription>{message}</AlertDescription>
  </Alert>
);

export default CallResultsDisplay;
